import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert, 
    Modal, Form, Tooltip, OverlayTrigger 
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';

export default function DetailBonLivraison() {
    const navigate = useNavigate();
    const { id } = useParams();
    const printRef = useRef();
    
    const [bl, setBl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [societeInfos, setSocieteInfos] = useState(null);
    
    // États pour les modals
    const [showLivraisonModal, setShowLivraisonModal] = useState(false);
    const [showAnnulationModal, setShowAnnulationModal] = useState(false);
    const [showPreparationModal, setShowPreparationModal] = useState(false);
    
    // États pour les alertes
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    
    // État pour les informations de livraison
    const [livraisonInfo, setLivraisonInfo] = useState({
        date_livraison_reelle: new Date().toISOString().split('T')[0],
        commentaire: '',
        nom_livreur: '',
        signature: false
    });

    useEffect(() => {
        fetchBonLivraison();
        fetchSocieteInfos();
    }, [id]);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=societe',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const fetchBonLivraison = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setBl(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleMarquerLivre = async () => {
        setSaving(true);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/bons_livraison.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'marquer_livre',
                        id: id,
                        date_livraison_reelle: livraisonInfo.date_livraison_reelle,
                        nom_livreur: livraisonInfo.nom_livreur,
                        commentaire: livraisonInfo.commentaire
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowLivraisonModal(false);
                fetchBonLivraison();
                showSuccess(" Bon de livraison marqué comme livré");
            } else {
                showError(result.message || "Erreur lors de la mise à jour");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleAnnuler = async () => {
        setSaving(true);
        
        try {
            const token = localStorage.getItem("token");
            
            const getResponse = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!getResponse.ok) throw new Error("Erreur chargement");
            
            const getResult = await getResponse.json();
            const blData = getResult.data;
            
            const updateResponse = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...blData,
                        statut: 'annule'
                    })
                }
            );

            const updateResult = await updateResponse.json();

            if (updateResult.success) {
                setShowAnnulationModal(false);
                fetchBonLivraison();
                showSuccess("❌ Bon de livraison annulé");
            } else {
                showError(updateResult.message || "Erreur lors de l'annulation");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const handlePreparer = async () => {
        setSaving(true);
        
        try {
            const token = localStorage.getItem("token");
            
            const getResponse = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!getResponse.ok) throw new Error("Erreur chargement");
            
            const getResult = await getResponse.json();
            const blData = getResult.data;
            
            const updateResponse = await fetch(
                `https://sawd.julesferry-ga.com/api/bons_livraison.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...blData,
                        statut: 'prepare'
                    })
                }
            );

            const updateResult = await updateResponse.json();

            if (updateResult.success) {
                setShowPreparationModal(false);
                fetchBonLivraison();
                showSuccess("📦 Bon de livraison marqué comme préparé");
            } else {
                showError(updateResult.message || "Erreur lors de la préparation");
            }
        } catch (err) {
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    // Fonction d'impression avec le même format que les factures
    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        
        const company = {
            name: societeInfos?.nom || 'GESTION STOCK',
            address: societeInfos?.adresse || '123 Rue du Commerce, Libreville',
            phone: societeInfos?.telephone || '+241 01 23 45 67',
            email: societeInfos?.email || 'contact@gestionstock.ga',
            site_web: societeInfos?.site_web || 'www.gestionstock.ga',
            rc: societeInfos?.rc || 'LB/2025/00123',
            nif: societeInfos?.nif || '20251234567',
            patente: societeInfos?.patente || 'PAT0012345',
            logo_url: societeInfos?.logo_url 
        };

        const totalProduits = bl?.lignes?.reduce((acc, l) => acc + l.quantite, 0) || 0;
        const statut = bl?.statut === 'livre' ? 'LIVRÉ' : 
                      bl?.statut === 'prepare' ? 'PRÉPARÉ' : 
                      bl?.statut === 'annule' ? 'ANNULÉ' : 'BROUILLON';
        
        const statutColor = bl?.statut === 'livre' ? '#10b981' :
                           bl?.statut === 'prepare' ? '#3b82f6' :
                           bl?.statut === 'annule' ? '#ef4444' : '#6b7280';

        const logoText = company.name.substring(0, 2).toUpperCase();
        const logoSrc = company.logo_url || '';

        const printStyles = `
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }

            body {
                background: #f5f7fa;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
                padding: 30px;
                color: #2c3e50;
            }

            .document-paper {
                max-width: 900px;
                margin: 0 auto;
                background: white;
                box-shadow: 0 5px 20px rgba(0,0,0,0.1);
                padding: 30px 35px;
                border-radius: 8px;
            }

            /* En-tête */
            .document-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 25px;
                padding-bottom: 20px;
                border-bottom: 2px solid ${statutColor};
            }

            .logo {
                width: 80px;
                height: 80px;
                object-fit: contain;
            }
            .logo-fallback {
                width: 80px;
                height: 80px;
                background: linear-gradient(135deg, ${statutColor}, ${statutColor}dd);
                color: white;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: bold;
                font-size: 2rem;
                border-radius: 8px;
                box-shadow: 0 2px 10px ${statutColor}50;
            }

            .company-name {
                font-size: 1.8rem;
                font-weight: 700;
                color: #1e293b;
                margin: 0;
            }

            .company-website {
                font-size: 0.8rem;
                color: #64748b;
            }

            .document-badge {
                text-align: right;
            }

            .doc-type {
                font-size: 1.2rem;
                font-weight: 600;
                color: ${statutColor};
                margin-bottom: 5px;
            }

            .doc-number {
                font-size: 0.9rem;
                color: #5a6a7e;
                background: #f1f5f9;
                padding: 4px 12px;
                border-radius: 4px;
                display: inline-block;
            }

            .status-badge {
                display: inline-block;
                padding: 4px 12px;
                border-radius: 4px;
                font-size: 0.8rem;
                font-weight: 600;
                text-transform: uppercase;
                margin-top: 8px;
                background: ${statutColor};
                color: white;
            }

            /* Section client */
            .client-section {
                background: #f8fafc;
                padding: 10px;
                border-radius: 6px;
                margin: 0px 0;
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
            }

            .client-info h3 {
                font-size: 0.8rem;
                font-weight: 600;
                color: ${statutColor};
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-bottom: 12px;
            }

            .client-name {
                font-size: 1.2rem;
                font-weight: 600;
                color: #1e293b;
                margin-bottom: 8px;
            }

            .client-details {
                font-size: 0.85rem;
                color: #4a5a6e;
                line-height: 1.6;
            }

            .client-details span {
                display: block;
            }

            .document-dates {
                text-align: right;
            }

            .date-item {
                margin-bottom: 8px;
            }

            .date-item .label {
                font-size: 0.75rem;
                color: #6f7d95;
                display: block;
            }

            .date-item .value {
                font-size: 1rem;
                font-weight: 500;
                color: #1e293b;
            }

            /* Informations livraison */
            .delivery-info {
                background: #f8fafc;
                padding: 20px;
                border-radius: 6px;
                margin: 20px 0;
                border-left: 4px solid ${statutColor};
            }

            .delivery-title {
                font-size: 1rem;
                font-weight: 600;
                color: ${statutColor};
                margin-bottom: 15px;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .delivery-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
            }

            .delivery-item {
                font-size: 0.85rem;
            }

            .delivery-item strong {
                color: #4b5563;
                display: block;
                margin-bottom: 4px;
                font-size: 0.75rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            /* Informations livreur */
            .livreur-info {
                background: #f8fafc;
                padding: 20px;
                border-radius: 6px;
                margin: 20px 0;
                border-left: 4px solid ${statutColor};
            }

            .livreur-title {
                font-size: 1rem;
                font-weight: 600;
                color: ${statutColor};
                margin-bottom: 15px;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .livreur-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
            }

            .livreur-item {
                font-size: 0.85rem;
            }

            .livreur-item strong {
                color: #4b5563;
                display: block;
                margin-bottom: 4px;
                font-size: 0.75rem;
                text-transform: uppercase;
                letter-spacing: 0.5px;
            }

            /* Tableau */
            .table-container {
                margin: 25px 0;
                border: 1px solid #e2e8f0;
                border-radius: 6px;
                overflow: hidden;
            }

            table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.85rem;
            }

            th {
                background: #f1f5f9;
                color: #1e293b;
                font-weight: 600;
                padding: 12px 10px;
                text-align: left;
                border-bottom: 1px solid #cbd5e1;
            }

            td {
                padding: 10px;
                border-bottom: 1px solid #e2e8f0;
            }

            tr:last-child td {
                border-bottom: none;
            }

            .ref-cell {
                font-family: 'Courier New', monospace;
                font-size: 0.8rem;
                color: ${statutColor};
                background: ${statutColor}20;
                padding: 3px 8px;
                border-radius: 3px;
                display: inline-block;
            }

            .text-center {
                text-align: center;
            }

            .text-end {
                text-align: right;
            }

            /* Totaux */
            .totals-wrapper {
                display: flex;
                justify-content: flex-end;
                margin-top: 20px;
            }

            .totals-card {
                width: 300px;
            }

            .total-line {
                display: flex;
                justify-content: space-between;
                padding: 8px 0;
                border-bottom: 1px dashed #d1d9e6;
            }

            .total-line:last-child {
                border-bottom: none;
            }

            .total-line .label {
                color: #4a5a6e;
            }

            .total-line .value {
                font-weight: 500;
            }

            .total-line.highlight {
                background: ${statutColor}20;
                margin-top: 8px;
                padding: 12px;
                border-radius: 4px;
                font-weight: 600;
            }

            .total-line.highlight .label,
            .total-line.highlight .value {
                color: ${statutColor};
                font-size: 1.1rem;
            }

            /* Notes */
            .notes-section {
                background: #fef3c7;
                padding: 12px 15px;
                border-left: 3px solid #f59e0b;
                margin: 20px 0;
                font-size: 0.9rem;
                color: #92400e;
            }

            /* Signatures */
            .signatures {
                display: flex;
                justify-content: space-between;
                margin-top: 40px;
                padding: 0 20px;
            }

            .signature-box {
                width: 200px;
                text-align: center;
            }

            .signature-line {
                border-top: 1px dashed #000;
                padding-top: 5px;
                margin-top: 30px;
            }

            .signature-label {
                font-size: 0.7rem;
                color: #64748b;
            }

            /* Pied de page */
            .document-footer {
                margin-top: 35px;
                padding-top: 15px;
                border-top: 1px solid #d1d9e6;
                display: flex;
                justify-content: space-between;
                align-items: center;
                font-size: 0.75rem;
                color: #7f8c9f;
            }

            .legal-info span {
                margin-right: 20px;
            }

            @media print {
                body {
                    background: white;
                    padding: 0;
                }
                .document-paper {
                    box-shadow: none;
                    padding: 20px;
                }
                th {
                    background: #f1f5f9 !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            }
        `;

        const printContent = `
            <!DOCTYPE html>
            <html lang="fr">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Bon de livraison ${bl?.numero_bl}</title>
                <style>
                    ${printStyles}
                </style>
            </head>
            <body>
                <div class="document-paper">
                    <!-- En-tête avec logo -->
                    <div class="document-header">
                        <div style="display: flex; align-items: center; gap: 15px;">
                            ${logoSrc ? `
                                <img src="${logoSrc}" alt="Logo" class="logo" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                                <div class="logo-fallback" style="display: none;">${logoText}</div>
                            ` : `
                                <div class="logo-fallback">${logoText}</div>
                            `}
                            <div>
                                <div class="company-name">${company.name}</div>
                                <div class="company-website">${company.site_web}</div>
                            </div>
                        </div>
                        <div class="document-badge">
                            <div class="doc-type">BON DE LIVRAISON</div>
                            <div class="doc-number">N° ${bl?.numero_bl}</div>
                            <div class="status-badge">${statut}</div>
                        </div>
                    </div>

                    <!-- Coordonnées société -->
                    <div style="font-size: 0.85rem; color: #5a6a7e; margin: 15px 0 20px 0; padding-bottom: 15px; border-bottom: 2px solid ${statutColor};">
                        <span style="margin-right: 20px;">| ${company.address}</span>
                        <span style="margin-right: 20px;">| ${company.phone}</span>
                        <span style="margin-right: 20px;">| ✉ ${company.email}</span>
                    </div>

                    <!-- Client -->
                    <div class="client-section">
                        <div class="client-info">
                            <h3>CLIENT</h3>
                            <div class="client-name">${bl?.client_nom || 'Client non spécifié'}</div>
                            <div class="client-details">
                                <span>Code: ${bl?.code_client || ''}</span>
                                ${bl?.client_telephone ? `<span> ${bl.client_telephone}</span>` : ''}
                                ${bl?.client_email ? `<span>✉ ${bl.client_email}</span>` : ''}
                                ${bl?.client_adresse ? `<span> ${bl.client_adresse}</span>` : ''}
                            </div>
                        </div>
                        <div class="document-dates">
                            <div class="date-item">
                                <span class="label">Date de livraison</span>
                                <span class="value">${formatDate(bl?.date_livraison)}</span>
                            </div>
                            ${bl?.numero_facture ? `
                                <div class="date-item">
                                    <span class="label">Facture associée</span>
                                    <span class="value">${bl.numero_facture}</span>
                                </div>
                            ` : ''}
                        </div>
                    </div>

                    <!-- Informations livraison -->
                    <div class="delivery-info">
                        <div class="delivery-title">
                            <i></i> INFORMATIONS DE LIVRAISON
                        </div>
                        <div class="delivery-grid">
                            <div class="delivery-item">
                                <strong>Destinataire</strong>
                                ${bl?.destinataire || bl?.client_nom || 'Non spécifié'}
                            </div>
                            <div class="delivery-item">
                                <strong>Adresse</strong>
                                ${bl?.adresse_livraison || bl?.client_adresse || 'Non spécifiée'}
                            </div>
                        </div>
                    </div>

                    <!-- Informations livreur (si disponibles) -->
                    ${bl?.nom_livreur || bl?.commentaire ? `
                        <div class="livreur-info">
                            <div class="livreur-title">
                                <i></i> INFORMATIONS LIVREUR
                            </div>
                            <div class="delivery-grid">
                                ${bl?.nom_livreur ? `  
                                    <div class="delivery-item">
                                        <strong>Nom du livreur</strong>
                                        ${bl.nom_livreur}
                                    </div>
                                ` : ''}
                                ${bl?.commentaire ? `
                                    <div class="delivery-item">
                                        <strong>Commentaire</strong>
                                        ${bl.commentaire}
                                    </div>
                                ` : ''}
                            </div>
                        </div>
                    ` : ''}

                   

                    <!-- Tableau des produits -->
                    <div class="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Réf.</th>
                                    <th>Désignation</th>
                                    <th class="text-center">Qté</th>
                                    <th>Lot</th>
                                    <th>Péremption</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${bl?.lignes && bl.lignes.length > 0 
                                    ? bl.lignes.map(ligne => `
                                        <tr>
                                            <td><span class="ref-cell">${ligne.reference || ''}</span></td>
                                            <td>${ligne.designation || ''}</td>
                                            <td class="text-center fw-bold">${ligne.quantite || 0}</td>
                                            <td>${ligne.lot || '-'}</td>
                                            <td>${ligne.date_peremption ? formatDate(ligne.date_peremption) : '-'}</td>
                                        </tr>
                                    `).join('')
                                    : '<tr><td colspan="5" style="text-align: center; padding: 20px;">Aucun produit dans ce bon de livraison</td></tr>'
                                }
                            </tbody>
                        </table>
                    </div>

                    <!-- Totaux -->
                    <div class="totals-wrapper">
                        <div class="totals-card">
                            <div class="total-line">
                                <span class="label">Nombre de références</span>
                                <span class="value">${bl?.lignes?.length || 0}</span>
                            </div>
                            <div class="total-line highlight">
                                <span class="label">Quantité totale</span>
                                <span class="value">${totalProduits}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Signatures -->
                    <div class="signatures">
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">Cachet et signature du transporteur</div>
                        </div>
                        <div class="signature-box">
                            <div class="signature-line"></div>
                            <div class="signature-label">Cachet et signature du client</div>
                        </div>
                    </div>

                    <!-- Pied de page -->
                    <div class="document-footer">
                        <div class="legal-info">
                            <span>RC: ${company.rc}</span>
                            <span>NIF: ${company.nif}</span>
                            <span>Patente: ${company.patente}</span>
                        </div>
                        <div>
                            Document généré le ${new Date().toLocaleDateString('fr-FR')}
                        </div>
                    </div>
                </div>
                
                <script>
                    window.onload = function() { 
                        setTimeout(function() {
                            window.print();
                        }, 500);
                    }
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: 'secondary', icon: 'ri-draft-line', label: 'Brouillon' },
            prepare: { color: 'primary', icon: 'ri-stack-line', label: 'Préparé' },
            livre: { color: 'success', icon: 'ri-check-double-line', label: 'Livré' },
            annule: { color: 'danger', icon: 'ri-close-circle-line', label: 'Annulé' }
        };
        const config = configs[statut] || configs.brouillon;
        return (
            <Badge 
                bg={config.color} 
                className="px-4 py-2 fs-6 statut-badge"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.color}), var(--bs-${config.color}-dark))`,
                    border: 'none',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
            >
                <i className={`${config.icon} me-2`}></i>
                {config.label}
            </Badge>
        );
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                        <p className="mt-3 text-muted">Chargement du bon de livraison...</p>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (error || !bl) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error || "Bon de livraison non trouvé"}</h4>
                        <Button 
                            variant="outline-danger" 
                            className="mt-3"
                            onClick={() => navigate('/ventes/blListe')}
                        >
                            <i className="ri-arrow-left-line me-2"></i>
                            Retour aux BL
                        </Button>
                    </Alert>
                </div>
            </React.Fragment>
        );
    }

    const totalProduits = bl.lignes?.reduce((acc, l) => acc + l.quantite, 0) || 0;

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4" ref={printRef}>
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <div className="page-header-modern mb-4 fade-in">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-3">
                                <div 
                                    className="page-icon-wrapper d-flex align-items-center justify-content-center bg-primary-soft" 
                                    style={{ 
                                        width: '70px', 
                                        height: '70px', 
                                        borderRadius: '20px',
                                        background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                        boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}
                                >
                                    <i className="ri-truck-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                </div>
                                <div>
                                    <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                        Détail du bon de livraison
                                    </h1>
                                    <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                        Consultez et gérez les informations du bon de livraison
                                    </p>
                                </div>
                            </div>
                            <div className="d-flex gap-2 no-print">
                                <ActionButton
                                    variant="outline-secondary"
                                    icon="ri-arrow-left-line"
                                    onClick={() => navigate('/ventes/blListe')}
                                    tooltip="Retour à la liste"
                                />
                                <ActionButton
                                    variant="outline-primary"
                                    icon="ri-printer-line"
                                    onClick={handlePrint}
                                    tooltip="Imprimer"
                                />
                                {bl.statut === 'brouillon' && (
                                    <>
                                        <ActionButton
                                            variant="outline-primary"
                                            icon="ri-pencil-line"
                                            onClick={() => navigate(`/ventes/bl/modifier/${bl.id_bon_livraison}`)}
                                            tooltip="Modifier"
                                        />
                                        <ActionButton
                                            variant="outline-success"
                                            icon="ri-stack-line"
                                            onClick={() => setShowPreparationModal(true)}
                                            tooltip="Préparer"
                                        />
                                    </>
                                )}
                                {bl.statut === 'prepare' && (
                                    <ActionButton
                                        variant="outline-success"
                                        icon="ri-check-double-line"
                                        onClick={() => setShowLivraisonModal(true)}
                                        tooltip="Marquer comme livré"
                                    />
                                )}
                                {bl.statut !== 'livre' && bl.statut !== 'annule' && (
                                    <ActionButton
                                        variant="outline-danger"
                                        icon="ri-close-circle-line"
                                        onClick={() => setShowAnnulationModal(true)}
                                        tooltip="Annuler"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Carte d'identité avec icônes plus grandes */}
                    <Card className="border-0 shadow-sm mb-4 facture-identity-card">
                        <Card.Body>
                            <Row className="align-items-center">
                                <Col md={4} className="border-end">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '60px', 
                                                height: '60px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                                boxShadow: '0 8px 16px rgba(67, 97, 238, 0.15)',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-barcode-line text-primary" style={{ fontSize: '30px' }}></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block mb-1">N° Bon de livraison</small>
                                            <h4 className="mb-0 fw-bold">{bl.numero_bl}</h4>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3} className="border-end">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '60px', 
                                                height: '60px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.15))',
                                                boxShadow: '0 8px 16px rgba(16, 185, 129, 0.15)',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-calendar-line text-success" style={{ fontSize: '30px' }}></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block mb-1">Date livraison</small>
                                            <h5 className="mb-0 fw-bold">{formatDate(bl.date_livraison)}</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3} className="border-end">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="d-flex align-items-center justify-content-center me-3" 
                                            style={{ 
                                                width: '60px', 
                                                height: '60px', 
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.15))',
                                                boxShadow: '0 8px 16px rgba(59, 130, 246, 0.15)',
                                                flexShrink: 0
                                            }}
                                        >
                                            <i className="ri-stack-line text-info" style={{ fontSize: '30px' }}></i>
                                        </div>
                                        <div>
                                            <small className="text-muted d-block mb-1">Articles</small>
                                            <h5 className="mb-0 fw-bold">{bl.lignes?.length || 0} produits</h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="text-end">
                                        {getStatutBadge(bl.statut)}
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Informations client et livraison */}
                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <div className="d-flex align-items-center">
                                        <div className="section-icon">
                                            <i className="ri-user-line text-primary"></i>
                                        </div>
                                        <h5 className="mb-0 ms-2">Informations client</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    <div className="client-info">
                                        <h4 className="fw-bold mb-3">{bl.client_nom}</h4>
                                        <p className="mb-2">
                                            <i className="ri-barcode-line text-muted me-2"></i>
                                            Code: {bl.code_client}
                                        </p>
                                        {bl.client_telephone && (
                                            <p className="mb-2">
                                                <i className="ri-phone-line text-muted me-2"></i>
                                                {bl.client_telephone}
                                            </p>
                                        )}
                                        {bl.client_email && (
                                            <p className="mb-2">
                                                <i className="ri-mail-line text-muted me-2"></i>
                                                {bl.client_email}
                                            </p>
                                        )}
                                        <p className="mb-0">
                                            <i className="ri-map-pin-line text-muted me-2"></i>
                                            {bl.client_adresse || 'Adresse non spécifiée'}
                                        </p>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <div className="d-flex align-items-center">
                                        <div className="section-icon">
                                            <i className="ri-truck-line text-primary"></i>
                                        </div>
                                        <h5 className="mb-0 ms-2">Informations de livraison</h5>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <div className="bg-light p-3 rounded">
                                                <small className="text-muted d-block">Destinataire</small>
                                                <strong>{bl.destinataire || bl.client_nom}</strong>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="bg-light p-3 rounded">
                                                <small className="text-muted d-block">Facture associée</small>
                                                <strong>{bl.numero_facture || 'Aucune'}</strong>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div className="bg-light p-3 rounded">
                                                <small className="text-muted d-block">Adresse de livraison</small>
                                                <strong>{bl.adresse_livraison || bl.client_adresse || 'Non spécifiée'}</strong>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Informations livreur (si disponibles) */}
                    {(bl.nom_livreur || bl.commentaire) && (
                        <Row className="mb-4">
                            <Col md={12}>
                                <Card className="border-0 shadow-sm">
                                    <Card.Header className="bg-transparent border-0 pt-4">
                                        <div className="d-flex align-items-center">
                                            <div className="section-icon">
                                                <i className="ri-user-star-line text-primary"></i>
                                            </div>
                                            <h5 className="mb-0 ms-2">Informations livreur</h5>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            {bl.nom_livreur && (
                                                <Col md={6}>
                                                    <div className="bg-light p-3 rounded">
                                                        <small className="text-muted d-block">Nom du livreur</small>
                                                        <strong>{bl.nom_livreur}</strong>
                                                    </div>
                                                </Col>
                                            )}
                                            {bl.commentaire && (
                                                <Col md={6}>
                                                    <div className="bg-light p-3 rounded">
                                                        <small className="text-muted d-block">Commentaire</small>
                                                        <strong>{bl.commentaire}</strong>
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Notes */}
                    {bl.notes && (
                        <Card className="border-0 shadow-sm mb-4">
                            <Card.Header className="bg-transparent border-0 pt-4">
                                <div className="d-flex align-items-center">
                                    <div className="section-icon">
                                        <i className="ri-file-text-line text-primary"></i>
                                    </div>
                                    <h5 className="mb-0 ms-2">Notes</h5>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-0">{bl.notes}</p>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Lignes du BL */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-transparent border-0 pt-4">
                            <div className="d-flex align-items-center">
                                <div className="section-icon">
                                    <i className="ri-shopping-cart-line text-primary"></i>
                                </div>
                                <h5 className="mb-0 ms-2">Produits à livrer</h5>
                                <Badge bg="info" className="ms-3 px-3 py-2">
                                    Total: {totalProduits} unités
                                </Badge>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="mb-0 align-middle modern-table">
                                    <thead>
                                        <tr>
                                            <th>Référence</th>
                                            <th>Désignation</th>
                                            <th className="text-center">Quantité</th>
                                            <th>Lot</th>
                                            <th>Date péremption</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bl.lignes?.map((ligne, index) => (
                                            <tr key={index} className="product-row">
                                                <td>
                                                    <Badge bg="light" text="dark" className="font-mono px-3 py-2">
                                                        {ligne.reference}
                                                    </Badge>
                                                </td>
                                                <td className="fw-semibold">{ligne.designation}</td>
                                                <td className="text-center fw-bold">{ligne.quantite}</td>
                                                <td>{ligne.lot || '-'}</td>
                                                <td>{ligne.date_peremption ? formatDate(ligne.date_peremption) : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* MODAL DE CONFIRMATION DE LIVRAISON */}
            <Modal show={showLivraisonModal} onHide={() => setShowLivraisonModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="success-icon mb-3">
                                <i className="ri-check-double-line text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Confirmer la livraison</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <p className="text-center text-muted mb-4">
                        Êtes-vous sûr de vouloir marquer ce bon de livraison comme <strong className="text-success">livré</strong> ?
                    </p>
                    
                    {/* <Alert variant="info" className="mb-4">
                        <div className="d-flex align-items-center">
                            <i className="ri-information-line fs-4 me-3"></i>
                            <div>
                                <small>BL: <strong>{bl?.numero_bl}</strong></small><br />
                                <small>Client: <strong>{bl?.client_nom}</strong></small>
                            </div>
                        </div>
                    </Alert> */}

                    <Form.Group className="mb-3">
                        <Form.Label>Date de livraison réelle</Form.Label>
                        <Form.Control
                            type="date"
                            value={livraisonInfo.date_livraison_reelle}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, date_livraison_reelle: e.target.value})}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Nom du livreur</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Nom du transporteur / livreur"
                            value={livraisonInfo.nom_livreur}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, nom_livreur: e.target.value})}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Commentaire (optionnel)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={livraisonInfo.commentaire}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, commentaire: e.target.value})}
                            placeholder="Observations sur la livraison..."
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            id="signature"
                            label="Je confirme que la livraison a été effectuée"
                            checked={livraisonInfo.signature}
                            onChange={(e) => setLivraisonInfo({...livraisonInfo, signature: e.target.checked})}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowLivraisonModal(false)}
                        className="px-4"
                    >
                        Annuler
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={handleMarquerLivre} 
                        disabled={saving || !livraisonInfo.signature}
                        className="px-5"
                    >
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Confirmation...
                            </>
                        ) : (
                            <>
                                <i className="ri-check-double-line me-2"></i>
                                Confirmer la livraison
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL DE CONFIRMATION DE PRÉPARATION */}
            <Modal show={showPreparationModal} onHide={() => setShowPreparationModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="success-icon mb-3">
                                <i className="ri-stack-line text-primary" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold">Préparer le bon</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    <p className="text-muted mb-4">
                        Confirmez-vous la préparation de ce bon de livraison ?
                    </p>
                    <Alert variant="info" className="text-start">
                        <i className="ri-information-line me-2"></i>
                        Une fois préparé, les produits seront déduits du stock.
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowPreparationModal(false)}
                        className="px-4"
                    >
                        Annuler
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handlePreparer} 
                        disabled={saving}
                        className="px-5"
                    >
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Préparation...
                            </>
                        ) : (
                            <>
                                <i className="ri-stack-line me-2"></i>
                                Confirmer la préparation
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL DE CONFIRMATION D'ANNULATION */}
            <Modal show={showAnnulationModal} onHide={() => setShowAnnulationModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="success-icon mb-3">
                                <i className="ri-close-circle-line text-danger" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-danger">Annuler le bon</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    <p className="text-muted mb-4">
                        Êtes-vous sûr de vouloir annuler ce bon de livraison ?
                    </p>
                    <p className="text-danger small">
                        Cette action est irréversible.
                    </p>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowAnnulationModal(false)}
                        className="px-4"
                    >
                        Retour
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleAnnuler} 
                        disabled={saving}
                        className="px-5"
                    >
                        {saving ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Annulation...
                            </>
                        ) : (
                            <>
                                <i className="ri-close-circle-line me-2"></i>
                                Confirmer l'annulation
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .success-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                
                .modern-modal .modal-header {
                    border-bottom: none;
                }
                
                .modern-modal .modal-footer {
                    border-top: none;
                    justify-content: center;
                    padding-bottom: 1.5rem;
                }
                
                .modern-modal .btn {
                    min-width: 120px;
                    padding: 0.6rem 1rem;
                    border-radius: 10px;
                }

                .page-header-modern {
                    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
                    padding: 1.5rem;
                    border-radius: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .page-title {
                    font-size: 1.6rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }

                .page-subtitle {
                    color: #64748b;
                    font-size: 0.95rem;
                }

                .facture-identity-card {
                    border-radius: 16px !important;
                    background: white;
                }

                .section-icon {
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(135deg, #4361ee20, #80b3ff20);
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.3rem;
                }

                .modern-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem;
                    border-bottom: 2px solid #e2e8f0;
                }

                .modern-table tbody td {
                    padding: 1rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #e2e8f0;
                }

                .product-row {
                    transition: all 0.2s ease;
                }

                .product-row:hover {
                    background: #f8fafc;
                }

                .action-btn {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }

                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }

                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }

                @media print {
                    .no-print {
                        display: none !important;
                    }
                }
            `}</style>
        </React.Fragment>
    );
}      